import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Megaphone, Plus, Pin, PinOff, X, Trash2, AlertTriangle, CheckCircle2,
  Building2, Users, Shield, Bell, Loader2, Edit3,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";

const iconFor = (v) => (v === "warning" ? AlertTriangle : v === "success" ? CheckCircle2 : Megaphone);
const styleFor = (v) =>
  v === "warning"
    ? "bg-amber-50 border-amber-300 text-amber-900"
    : v === "success"
    ? "bg-emerald-50 border-emerald-300 text-emerald-900"
    : "bg-sky-50 border-sky-300 text-sky-900";

const audienceLabel = (a) => {
  if (a?.audience === "leadership") return { label: "Leadership", Icon: Shield, cls: "bg-purple-100 text-purple-800 border-purple-200" };
  if (a?.audience === "department") return { label: a.target_department || "Department", Icon: Building2, cls: "bg-orange-100 text-orange-800 border-orange-200" };
  return { label: "All employees", Icon: Users, cls: "bg-emerald-100 text-emerald-800 border-emerald-200" };
};

function AudienceBadge({ a }) {
  const { label, Icon, cls } = audienceLabel(a);
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border font-mono text-[10px] uppercase tracking-widest ${cls}`}>
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
}

/**
 * /announcements — full board.
 * Leaders (crew_chief, Supervisor, Manager, Senior Manager, admin) get an
 * inline composer at the top. Managers/Supervisors/Crew Chiefs can only
 * post to their own department; Senior Managers + Admins can also post to
 * `all` or `leadership`.
 */
export default function Announcements() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);

  const isFullReach = user?.role === "admin" || user?.job_title === "Senior Manager";
  const canPost =
    user?.role === "admin" ||
    user?.role === "crew_chief" ||
    ["Supervisor", "Manager", "Senior Manager"].includes(user?.job_title);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(canPost ? "/announcements" : "/announcements/active");
      setItems(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load announcements");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    api.get("/departments").then((r) => setDepartments((r.data || []).map((d) => d.name || d))).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = async (id) => {
    setItems((arr) => arr.map((a) => (a.id === id ? { ...a, dismissed_by_me: true } : a)));
    try { await api.post(`/announcements/${id}/dismiss`); } catch { /* keep client optimistic */ }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this announcement? This cannot be undone.")) return;
    try {
      await api.delete(`/announcements/${id}`);
      toast.success("Announcement deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const togglePin = async (a) => {
    try {
      await api.patch(`/announcements/${a.id}`, { pinned: !a.pinned });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const toggleActive = async (a) => {
    try {
      await api.patch(`/announcements/${a.id}`, { active: !a.active });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  return (
    <div className="w-full mx-auto px-4 sm:px-[15%] py-8" data-testid="announcements-page">
      <div className="flex items-center gap-3">
        <Megaphone className="w-6 h-6 text-summit-primary" />
        <h1 className="font-display text-3xl font-medium tracking-tight" data-testid="announcements-page-title">
          Announcements
        </h1>
      </div>
      <p className="text-sm text-gray-500 mt-1 max-w-xl">
        Company-wide updates, safety notes, and department bulletins from leadership.
      </p>

      {canPost && (
        <ComposerCard
          isFullReach={isFullReach}
          userDepartment={user?.department}
          departments={departments}
          onPosted={load}
        />
      )}

      <div className="mt-8 space-y-3" data-testid="announcements-list">
        {loading ? (
          <div className="py-12 text-center text-sm text-gray-400">
            <Loader2 className="w-4 h-4 mx-auto animate-spin mb-2" /> Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="py-12 text-center text-sm text-gray-400" data-testid="announcements-empty">
            No announcements yet.
          </div>
        ) : (
          items.map((a) => {
            const Icon = iconFor(a.variant);
            return (
              <div
                key={a.id}
                className={`border rounded-lg px-4 py-3 ${styleFor(a.variant)} ${!a.active ? "opacity-50" : ""}`}
                data-testid={`announcement-item-${a.id}`}
              >
                <div className="flex items-start gap-3">
                  <Icon className="w-4 h-4 mt-1 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{a.title}</span>
                      {a.pinned && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border font-mono text-[9px] uppercase tracking-widest bg-white/60 border-current">
                          <Pin className="w-3 h-3" /> Pinned
                        </span>
                      )}
                      <AudienceBadge a={a} />
                      {!a.active && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border font-mono text-[9px] uppercase tracking-widest bg-white/60 border-current">
                          Inactive
                        </span>
                      )}
                    </div>
                    <div className="text-sm mt-1 whitespace-pre-wrap opacity-90">{a.body}</div>
                    <div className="font-mono text-[10px] uppercase tracking-widest opacity-70 mt-2">
                      — {a.author_name} · {a.author_job_title} · {new Date(a.created_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {canPost && (
                      <>
                        <button
                          onClick={() => togglePin(a)}
                          className="p-1.5 rounded hover:bg-white/50"
                          aria-label={a.pinned ? "Unpin" : "Pin"}
                          title={a.pinned ? "Unpin" : "Pin"}
                          data-testid={`announcement-pin-${a.id}`}
                        >
                          {a.pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => toggleActive(a)}
                          className="p-1.5 rounded hover:bg-white/50 text-xs font-mono uppercase tracking-widest"
                          title={a.active ? "Archive" : "Activate"}
                          data-testid={`announcement-active-${a.id}`}
                        >
                          {a.active ? "Hide" : "Show"}
                        </button>
                        <button
                          onClick={() => remove(a.id)}
                          className="p-1.5 rounded hover:bg-white/50 text-red-700"
                          aria-label="Delete"
                          title="Delete"
                          data-testid={`announcement-delete-${a.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                    {!a.dismissed_by_me && (
                      <button
                        onClick={() => dismiss(a.id)}
                        className="p-1.5 rounded hover:bg-white/50"
                        aria-label="Dismiss"
                        title="Dismiss"
                        data-testid={`announcement-dismiss-${a.id}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function ComposerCard({ isFullReach, userDepartment, departments, onPosted }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [variant, setVariant] = useState("info");
  const [audience, setAudience] = useState(isFullReach ? "all" : "department");
  const [target, setTarget] = useState(userDepartment || "");
  const [pinned, setPinned] = useState(false);
  const [sendPush, setSendPush] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!title.trim() || !body.trim()) { toast.error("Title and body required"); return; }
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        body: body.trim(),
        variant,
        audience,
        target_department: audience === "department" ? target : null,
        pinned,
        send_push: sendPush,
        active: true,
      };
      await api.post("/announcements", payload);
      toast.success("Announcement posted");
      setTitle(""); setBody(""); setPinned(false); setSendPush(false);
      onPosted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not post");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className="mt-8 border border-summit-hairline rounded-lg bg-white p-5"
      data-testid="announcements-composer"
    >
      <div className="flex items-center gap-2 mb-4">
        <Edit3 className="w-4 h-4 text-summit-primary" />
        <h2 className="font-display text-lg font-medium tracking-tight">New announcement</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Label className="text-xs font-mono uppercase tracking-widest text-gray-500">Title</Label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Winter operations kickoff"
            className="mt-1 border-summit-hairline"
            data-testid="announcements-composer-title"
          />
        </div>

        <div className="md:col-span-2">
          <Label className="text-xs font-mono uppercase tracking-widest text-gray-500">Message</Label>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={4}
            placeholder="What should the team know?"
            className="mt-1 w-full rounded-md border border-summit-hairline p-2 text-sm resize-y focus:outline-none focus:ring-1 focus:ring-summit-primary"
            data-testid="announcements-composer-body"
          />
        </div>

        <div>
          <Label className="text-xs font-mono uppercase tracking-widest text-gray-500">Style</Label>
          <Select value={variant} onValueChange={setVariant}>
            <SelectTrigger className="mt-1 border-summit-hairline" data-testid="announcements-composer-variant">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="info">Info · blue</SelectItem>
              <SelectItem value="warning">Warning · amber</SelectItem>
              <SelectItem value="success">Success · green</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-xs font-mono uppercase tracking-widest text-gray-500">Audience</Label>
          <Select value={audience} onValueChange={setAudience}>
            <SelectTrigger className="mt-1 border-summit-hairline" data-testid="announcements-composer-audience">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {isFullReach && <SelectItem value="all">All employees</SelectItem>}
              {isFullReach && <SelectItem value="leadership">Leadership only</SelectItem>}
              <SelectItem value="department">Department</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {audience === "department" && (
          <div className="md:col-span-2">
            <Label className="text-xs font-mono uppercase tracking-widest text-gray-500">Target department</Label>
            {isFullReach ? (
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger className="mt-1 border-summit-hairline" data-testid="announcements-composer-target">
                  <SelectValue placeholder="Choose department" />
                </SelectTrigger>
                <SelectContent>
                  {departments.map((d) => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                value={userDepartment || ""}
                readOnly
                className="mt-1 border-summit-hairline bg-gray-50"
                data-testid="announcements-composer-target-locked"
              />
            )}
          </div>
        )}

        <div className="flex items-center gap-2">
          <Switch checked={pinned} onCheckedChange={setPinned} data-testid="announcements-composer-pinned" />
          <Label className="text-xs font-mono uppercase tracking-widest text-gray-500 flex items-center gap-1">
            <Pin className="w-3 h-3" /> Pin (show on next login)
          </Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch checked={sendPush} onCheckedChange={setSendPush} data-testid="announcements-composer-push" />
          <Label className="text-xs font-mono uppercase tracking-widest text-gray-500 flex items-center gap-1">
            <Bell className="w-3 h-3" /> Send push notification
          </Label>
        </div>
      </div>

      <div className="mt-5">
        <Button
          onClick={submit}
          disabled={saving || !title.trim() || !body.trim() || (audience === "department" && !target && !userDepartment)}
          className="bg-summit-primary hover:bg-summit-primary-hover text-white"
          data-testid="announcements-composer-submit"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          <span className="ml-2 text-sm">{saving ? "Posting…" : "Post announcement"}</span>
        </Button>
      </div>
    </section>
  );
}
